"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.icpEnglishCreditOf = icpEnglishCreditOf;
exports.icpJevValueOf = icpJevValueOf;
exports.icpEnglishValueOf = icpEnglishValueOf;
exports.icpFactValueOf = icpFactValueOf;
exports.icpCandidateFactsOf = icpCandidateFactsOf;
exports.icpScoreOf = icpScoreOf;
exports.compareIcpScores = compareIcpScores;
exports.icpStaleItemIds = icpStaleItemIds;
exports.icpInterviewMicroUsdOf = icpInterviewMicroUsdOf;
exports.icpInterviewCentsOf = icpInterviewCentsOf;
exports.icpInterviewCostLabel = icpInterviewCostLabel;
exports.icpReadingEstimate = icpReadingEstimate;
const enums_1 = require("./enums");
const icp_1 = require("./icp");
const jev_1 = require("./jev");
// ADR 0061, PLAN §5 — the ideal candidate order, as pure functions the browser and the server share.
//
// The formula (PLAN §5.1), for one person, over the profile's included items i with rating r and credit c (hundredths):
//   T = Σ r · H = Σ round(100·r·c) · A = Σ r over known items · HA = Σ round(100·r·c) over known items
//   match = T = 0 ? null : round(H / T)        (unknowns count 0, ADR 0058 Q1 a)
//   fit = A = 0 ? null : round(HA / A) · covers = T = 0 ? 0 : round(100·A / T)
//   showsAllMusts = every included item rated 5 has meetsMust (unknown never meets)
// Order within the open tab: people Jev read first (when the profile has a Jev-read item), then showsAllMusts, then match
// descending, then the order passed in. People not read go last (ADR 0050 §4).
//
// Item values (PLAN §5.2, with the owner's answers of 2026-10-25 applied):
//   years (Q2 b, a RANGE): months null → unknown; within [min, max] → met (1); above max → ICP_YEARS_OVER_CREDIT; below min
//     by at most ICP_YEARS_NEAR → near (0.5); otherwise not met (0). No max means no top.
//   notice (Q5 a): days null → unknown; ≤ withinDays → met (1); ≤ withinDays × ICP_NOTICE_NEAR_FACTOR → near (0.5); else not met.
//   education, location, salary, English and Jev lines: as the plan's table.
// ---- Constants ----
/** English levels 0–3 map onto these credits (PLAN §5.2): a few small mistakes is not yet clear. */
const ENGLISH_CREDIT_STEPS = [0, 0.5, 0.85, 1];
const ENGLISH_LEVEL_MAX = ENGLISH_CREDIT_STEPS.length - 1;
/** A fact one step short of the ask: education one level under, years just under the minimum, salary a little over, notice a little long. */
const NEAR_CREDIT = 0.5;
const HUNDREDTHS = 100;
const MONTHS_PER_YEAR = 12;
const CREDIT_PRECISION = 1_000;
/** 100 × 3 × .675 is 202.49999999999997 in floating point; rounding to 6 places first keeps the half-way cases honest. */
const FLOAT_GUARD = 1_000_000;
const ONE_CENT_IN_MICRO_USD = 10_000;
const CENTS_PER_DOLLAR = 100;
const TOKENS_PER_THOUSAND = 1_000;
const TENTH_OF_A_CENT = 0.1;
/** Cents below this show one decimal; from here they show whole cents. */
const WHOLE_CENTS_FROM = 9.95;
/** Whole cents below this; from here dollars. */
const DOLLARS_FROM = 99.5;
/** The field the serialiser lists when it masked the row's salary mark (permissions.ts, candidate.expectedSalary). */
const SALARY_MASK_FIELD = 'expectedSalaryWithinBudget';
// ---- Values ----
function unknownValue(detail) {
    return { credit: 0, isKnown: false, meetsMust: false, isExcluded: false, mark: 'unknown', detail };
}
// Thousands of rows share these words, so they share the objects too (F23.04).
const NOT_READ_VALUE = unknownValue('Not read');
const NOT_GIVEN_VALUE = unknownValue('Not given');
/** Left out of the totals: it counts for nothing, not even as a must (PLAN §5.2). */
function excludedValue(mark, detail) {
    return { credit: 0, isKnown: false, meetsMust: false, isExcluded: true, mark, detail };
}
/** A fact's known step: only a full match meets a must. */
function stepValue(credit, mark, detail) {
    return { credit, isKnown: true, meetsMust: credit === 1, isExcluded: false, mark, detail };
}
function roundTo3(value) {
    return Math.round(value * CREDIT_PRECISION) / CREDIT_PRECISION;
}
/** English levels 0–3 map onto credit steps [0, .5, .85, 1], linearly between them. */
function icpEnglishCreditOf(level) {
    const clamped = Math.min(ENGLISH_LEVEL_MAX, Math.max(0, level));
    const below = Math.floor(clamped);
    const lower = ENGLISH_CREDIT_STEPS[below] ?? 0;
    const upper = ENGLISH_CREDIT_STEPS[Math.min(ENGLISH_LEVEL_MAX, below + 1)] ?? lower;
    return roundTo3(lower + (clamped - below) * (upper - lower));
}
function icpJevValueOf(answer, isRead) {
    if (!isRead)
        return NOT_READ_VALUE;
    if (!answer)
        return unknownValue('Jev gave no answer');
    const mark = (0, jev_1.jevEvidenceOf)(answer.level);
    // ADR 0058: a grade below the assessable line says nothing about the person, so it is unknown and earns nothing.
    if (answer.level < jev_1.JEV_ASSESSABLE_LEVEL)
        return { credit: 0, isKnown: false, meetsMust: false, isExcluded: false, mark, detail: 'The CV does not say' };
    return { credit: answer.credit, isKnown: true, meetsMust: answer.meets >= jev_1.JEV_MEETS_AT, isExcluded: false, mark, detail: null };
}
function icpEnglishValueOf(level, isRead) {
    if (!isRead)
        return NOT_READ_VALUE;
    if (level === null)
        return unknownValue('Jev did not rate the English');
    return { credit: icpEnglishCreditOf(level), isKnown: true, meetsMust: level >= icp_1.ICP_ENGLISH_MEETS_AT, isExcluded: false, mark: (0, jev_1.jevEnglishOf)(level), detail: null };
}
// ---- Facts ----
function yearsText(years) {
    const shown = Number.isInteger(years) ? String(years) : years.toFixed(1);
    return `${shown} ${years === 1 ? 'year' : 'years'}`;
}
function yearsNeeds(fact) {
    if (fact.maxYears === null)
        return `${fact.minYears}+`;
    if (fact.maxYears === fact.minYears)
        return `exactly ${fact.minYears}`;
    return `${fact.minYears}–${fact.maxYears}`;
}
/** Owner Q2 (b): a range. Under the minimum uses the near step, above the top counts half, no top means no top.
 * Above the top still meets a must-have (DECISIONS-LOG 2026-10-26): more experience never drops someone out of the top group, which would act as a hidden age filter. */
function yearsValueOf(fact, months) {
    const needs = `needs ${yearsNeeds(fact)}`;
    if (months === null)
        return unknownValue(`Not given · ${needs}`);
    const years = months / MONTHS_PER_YEAR;
    const detail = `${yearsText(years)} · ${needs}`;
    if (fact.maxYears !== null && years > fact.maxYears)
        return { credit: icp_1.ICP_YEARS_OVER_CREDIT, isKnown: true, meetsMust: true, isExcluded: false, mark: 'near', detail };
    if (years >= fact.minYears)
        return stepValue(1, 'met', detail);
    if (years >= fact.minYears - icp_1.ICP_YEARS_NEAR)
        return stepValue(NEAR_CREDIT, 'near', detail);
    return stepValue(0, 'not_met', detail);
}
/**
 * The record's education wins when it has one. With none, the job's own degree criterion stands in (DECISIONS-LOG 2026-10-26):
 * met only if the CV check marked a criterion at this level or above met; a failed or missing check leaves it "not given",
 * because a CV that doesn't say is not a no.
 */
function educationValueOf(minLevel, row) {
    const { education, cvDegree } = row;
    if (education === null) {
        if (cvDegree === null || enums_1.EDUCATION_LEVELS.indexOf(cvDegree) < enums_1.EDUCATION_LEVELS.indexOf(minLevel))
            return NOT_GIVEN_VALUE;
        return stepValue(1, 'met', `${enums_1.EDUCATION_LEVEL_LABELS[cvDegree]} (from the CV)`);
    }
    const gap = enums_1.EDUCATION_LEVELS.indexOf(minLevel) - enums_1.EDUCATION_LEVELS.indexOf(education);
    if (gap <= 0)
        return stepValue(1, 'met', null);
    return gap === 1 ? stepValue(NEAR_CREDIT, 'near', 'One level under') : stepValue(0, 'not_met', null);
}
function foldedWordsOf(text) {
    return text
        .normalize('NFKD')
        .replace(/\p{M}+/gu, '')
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((word) => word !== '');
}
// Thousands of rows share a few dozen cities, so each spelling is folded once (F23.04: 5,000 rows × 15 items in 100 ms).
const FOLDED_WORDS = new Map();
const FOLDED_WORDS_MAX = 5_000;
/** Case, accents and punctuation ignored, one word at a time, so "Agrabad, Chattogram" holds "chattogram" as a whole word. */
function wordsOf(text) {
    const cached = FOLDED_WORDS.get(text);
    if (cached)
        return cached;
    if (FOLDED_WORDS.size >= FOLDED_WORDS_MAX)
        FOLDED_WORDS.clear();
    const words = foldedWordsOf(text);
    FOLDED_WORDS.set(text, words);
    return words;
}
function containsWords(haystack, needle) {
    if (needle.length === 0 || needle.length > haystack.length)
        return false;
    for (let start = 0; start + needle.length <= haystack.length; start += 1) {
        if (needle.every((word, offset) => haystack[start + offset] === word))
            return true;
    }
    return false;
}
// A profile's places are folded once, not once per row: 5,000 rows read the same few words (F23.04).
const PLACE_WORDS = new WeakMap();
function placeWordsOf(fact) {
    const cached = PLACE_WORDS.get(fact);
    if (cached)
        return cached;
    const folded = [fact.city, ...fact.aliases].map(wordsOf).filter((words) => words.length > 0);
    PLACE_WORDS.set(fact, folded);
    return folded;
}
function locationValueOf(fact, row, job) {
    if (job.isRemote)
        return excludedValue('remote', 'Remote job');
    if (row.city === null)
        return unknownValue('City not given');
    const lives = wordsOf(row.city);
    if (placeWordsOf(fact).some((place) => containsWords(lives, place)))
        return stepValue(1, 'met', `Lives in ${row.city}`);
    if (row.isWillingToRelocate === true)
        return stepValue(NEAR_CREDIT, 'near', `Lives in ${row.city} · willing to relocate`);
    return stepValue(0, 'not_met', `Lives in ${row.city}`);
}
function salaryValueOf(row, job) {
    // Hidden is checked before everything else, so an order never shows a viewer who may not see salaries who is over budget (#5).
    if (row.isSalaryHidden)
        return excludedValue('hidden', 'Hidden from you');
    if (!job.hasBudget)
        return excludedValue('no_budget', 'The job has no budget top');
    if (row.salaryWithinBudget === null)
        return unknownValue('Expected salary not given');
    if (row.salaryWithinBudget)
        return stepValue(1, 'met', 'Within budget');
    const over = row.salaryOverBudgetPercent;
    if (over !== null && over <= icp_1.ICP_SALARY_NEAR_PERCENT)
        return stepValue(NEAR_CREDIT, 'near', `${over}% over budget`);
    return stepValue(0, 'not_met', over === null ? 'Over budget' : `${over}% over budget`);
}
function noticeValueOf(withinDays, days) {
    if (days === null)
        return unknownValue('Notice period not given');
    const detail = `${days} ${days === 1 ? 'day' : 'days'} notice`;
    if (days <= withinDays)
        return stepValue(1, 'met', detail);
    if (days <= withinDays * icp_1.ICP_NOTICE_NEAR_FACTOR)
        return stepValue(NEAR_CREDIT, 'near', detail);
    return stepValue(0, 'not_met', detail);
}
function icpFactValueOf(fact, row, job) {
    switch (fact.type) {
        case 'years':
            return yearsValueOf(fact, row.experienceMonths);
        case 'education':
            return educationValueOf(fact.minLevel, row);
        case 'location':
            return locationValueOf(fact, row, job);
        case 'salary':
            return salaryValueOf(row, job);
        case 'notice':
            return noticeValueOf(fact.withinDays, row.noticePeriodDays);
    }
}
/** The facts a row holds, with the salary left out for a viewer who can't see salaries. The degree falls back to the CV's words (`row.cvDegree`, ADR 0062 OQ2), no longer to criteria cells. */
function icpCandidateFactsOf(row, canSeeSalary) {
    const isSalaryHidden = !canSeeSalary || (row.application.restrictedFields?.includes(SALARY_MASK_FIELD) ?? false);
    return {
        city: row.city,
        experienceMonths: row.experienceMonths,
        education: row.education,
        cvDegree: row.cvDegree,
        isWillingToRelocate: row.isWillingToRelocate,
        noticePeriodDays: row.noticePeriodDays,
        isSalaryHidden,
        salaryWithinBudget: isSalaryHidden ? null : row.application.expectedSalaryWithinBudget,
        salaryOverBudgetPercent: isSalaryHidden ? null : row.application.expectedSalaryOverBudgetPercent,
    };
}
// ---- Score and order ----
/** Hundredths of a rating earned, as focusScore (F23.04): 79.5 rounds to 80. */
function hundredthsOf(rating, credit) {
    return Math.round(Math.round(HUNDREDTHS * rating * credit * FLOAT_GUARD) / FLOAT_GUARD);
}
function icpScoreOf(ratings, values, isRead) {
    let total = 0;
    let earned = 0;
    let knownTotal = 0;
    let knownEarned = 0;
    let showsAllMusts = true;
    values.forEach((value, index) => {
        if (value.isExcluded)
            return;
        const rating = ratings[index] ?? 0;
        const hundredths = hundredthsOf(rating, value.credit);
        total += rating;
        earned += hundredths;
        if (value.isKnown) {
            knownTotal += rating;
            knownEarned += hundredths;
        }
        // Unknown never meets a must (ADR 0058 Q1 a); the must is the rating itself, so lowering a rating frees the line.
        if (rating === icp_1.ICP_MUST_RATING && !value.meetsMust)
            showsAllMusts = false;
    });
    return {
        match: total === 0 ? null : Math.round(earned / total),
        fit: knownTotal === 0 ? null : Math.round(knownEarned / knownTotal),
        covers: total === 0 ? 0 : Math.round((HUNDREDTHS * knownTotal) / total),
        showsAllMusts,
        isRead,
    };
}
/** People Jev read first, every must-have met first, then the match; 0 keeps the order passed in. A person with no score goes last. */
function compareIcpScores(a, b) {
    const aRead = a?.isRead === true;
    const bRead = b?.isRead === true;
    if (aRead !== bRead)
        return aRead ? -1 : 1;
    if (!a || !b || !aRead)
        return 0;
    if (a.showsAllMusts !== b.showsAllMusts)
        return a.showsAllMusts ? -1 : 1;
    return (b.match ?? 0) - (a.match ?? 0);
}
// ---- Which of the profile's lines a Jev ask has read ----
/** The index of the ask's part that is this very line with these very words; -1 when the ask never read it. */
function askPartIndexOf(item, ask) {
    if (!ask?.icp)
        return -1;
    const index = ask.icp.itemIds.indexOf(item.id);
    return index >= 0 && ask.parts[index]?.text === (0, icp_1.icpPartTextOf)(item) ? index : -1;
}
/** Jev lines that are not in the settled ask, or whose words changed since: they are left out until read. */
function icpStaleItemIds(items, ask) {
    return items.filter((item) => item.kind === 'jev' && askPartIndexOf(item, ask) < 0).map((item) => item.id);
}
// ---- Words about cost ----
/** Sonnet 5.5's list price for an interview's tokens, in millionths of a dollar. The one place the interview is priced: the API and the labels on screen both call it, so cache reads and writes can't be priced two ways. */
function icpInterviewMicroUsdOf(tokens) {
    return Math.round((tokens.input * icp_1.SONNET_MICRO_USD_PER_THOUSAND_INPUT +
        tokens.output * icp_1.SONNET_MICRO_USD_PER_THOUSAND_OUTPUT +
        tokens.cacheRead * icp_1.SONNET_MICRO_USD_PER_THOUSAND_CACHE_READ +
        tokens.cacheWrite * icp_1.SONNET_MICRO_USD_PER_THOUSAND_CACHE_WRITE) /
        TOKENS_PER_THOUSAND);
}
/** US cents to one decimal: what the 15¢ cap (ADR 0061) is checked against. */
function icpInterviewCentsOf(tokens) {
    return Math.round((icpInterviewMicroUsdOf(tokens) / ONE_CENT_IN_MICRO_USD) * 10) / 10;
}
/** "0.9¢" under 10¢, "11¢" from 10¢, "$1.10" from 100¢, "under 0.1¢". */
function icpInterviewCostLabel(tokens) {
    const cents = icpInterviewMicroUsdOf(tokens) / ONE_CENT_IN_MICRO_USD;
    if (cents < TENTH_OF_A_CENT)
        return 'under 0.1¢';
    if (cents < WHOLE_CENTS_FROM)
        return `${cents.toFixed(1)}¢`;
    if (cents < DOLLARS_FROM)
        return `${Math.round(cents)}¢`;
    return `$${(cents / CENTS_PER_DOLLAR).toFixed(2)}`;
}
/** The ranking's cost is a guess, so it speaks in whole cents: "about 6¢", "under 1¢", "about $1.10". */
function readingCostWords(microUsd) {
    const cents = microUsd / ONE_CENT_IN_MICRO_USD;
    if (cents < 1)
        return 'under 1¢';
    if (cents < DOLLARS_FROM)
        return `about ${Math.round(cents)}¢`;
    return `about $${(cents / CENTS_PER_DOLLAR).toFixed(2)}`;
}
/** What ranking would read and cost, before it starts: "Ranking 96 CVs with Jev · about 6¢". */
function icpReadingEstimate(items, settled, readableCvs) {
    const newLines = icpStaleItemIds(items, settled).length;
    if (newLines === 0)
        return { newLines, tokens: 0, label: 'Nothing new for Jev to read' };
    if (readableCvs <= 0)
        return { newLines, tokens: 0, label: 'No CVs to read yet' };
    const tokens = readableCvs * (icp_1.JEV_TOKENS_PER_CV_ESTIMATE + newLines * icp_1.JEV_TOKENS_PER_PART_ESTIMATE);
    const microUsd = (tokens * jev_1.JEV_MICRO_USD_PER_THOUSAND_TOKENS) / TOKENS_PER_THOUSAND;
    return { newLines, tokens, label: `Ranking ${readableCvs} ${readableCvs === 1 ? 'CV' : 'CVs'} with Jev · ${readingCostWords(microUsd)}` };
}
//# sourceMappingURL=icp-scoring.js.map